export interface VideoRecorderOptions {
  mimeType?: string;
  videoBitsPerSecond?: number;
}

export class VideoRecorder {
  private mediaRecorder: MediaRecorder | null = null;
  private recordedChunks: Blob[] = [];
  private stream: MediaStream | null = null;

  constructor(private options: VideoRecorderOptions = {}) {
    this.options = {
      mimeType: 'video/webm;codecs=vp9',
      videoBitsPerSecond: 500000, // 500 Kbps for smaller file size
      ...options
    };
  }

  static isSupported(): boolean {
    return !!(
      navigator.mediaDevices &&
      typeof navigator.mediaDevices.getDisplayMedia === 'function' &&
      typeof MediaRecorder !== 'undefined'
    );
  }

  async startRecording(): Promise<void> {
    if (!VideoRecorder.isSupported()) {
      throw new Error('Screen recording is not supported in this browser');
    }

    try {
      // Request screen capture with preferCurrentTab hint for better UX
      // Add resolution constraints to reduce file size
      this.stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          cursor: 'always',
          displaySurface: 'browser',
          width: { ideal: 1280, max: 1920 },
          height: { ideal: 720, max: 1080 },
          frameRate: { ideal: 15, max: 30 } // Lower frame rate for smaller file size
        },
        audio: false,
        preferCurrentTab: true
      } as DisplayMediaStreamOptions);

      // Check if user cancelled
      if (!this.stream) {
        throw new Error('User cancelled screen recording');
      }

      // Create MediaRecorder
      const mimeType = this.getMimeType();
      this.mediaRecorder = new MediaRecorder(this.stream, {
        mimeType,
        videoBitsPerSecond: this.options.videoBitsPerSecond
      });

      this.recordedChunks = [];

      // Handle data available event
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.recordedChunks.push(event.data);
        }
      };

      // Handle stop event
      this.mediaRecorder.onstop = () => {
        // Clean up stream tracks
        if (this.stream) {
          this.stream.getTracks().forEach(track => track.stop());
          this.stream = null;
        }
      };

      // Handle errors
      this.mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event);
        this.stopRecording().catch(console.error);
      };

      // Monitor stream for unexpected stops
      if (this.stream) {
        const videoTrack = this.stream.getVideoTracks()[0];
        if (videoTrack) {
          videoTrack.onended = () => {
            console.log('Video track ended unexpectedly');
            if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
              this.mediaRecorder.stop();
            }
          };
        }
      }

      // Start recording with timeslice for regular data chunks
      this.mediaRecorder.start(1000); // Collect data every second
      console.log('Video recording started');
    } catch (error) {
      // Clean up on error
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop());
        this.stream = null;
      }
      throw error;
    }
  }

  async stopRecording(): Promise<Blob> {
    return new Promise((resolve, reject) => {
      if (!this.mediaRecorder) {
        reject(new Error('No recording in progress'));
        return;
      }

      const recorder = this.mediaRecorder;
      
      // Set up handlers before stopping
      recorder.onstop = () => {
        try {
          // Create blob from chunks
          const mimeType = this.getMimeType();
          const blob = new Blob(this.recordedChunks, { type: mimeType });
          
          // Clean up
          this.recordedChunks = [];
          this.mediaRecorder = null;
          
          // Stop all tracks
          if (this.stream) {
            this.stream.getTracks().forEach(track => track.stop());
            this.stream = null;
          }
          
          console.log('Video recording stopped, blob size:', blob.size);
          resolve(blob);
        } catch (error) {
          reject(error);
        }
      };

      recorder.onerror = (event) => {
        reject(new Error(`Recording error: ${event}`));
      };

      // Stop recording
      if (recorder.state === 'recording') {
        recorder.stop();
      } else {
        // Already stopped, resolve with current chunks
        const mimeType = this.getMimeType();
        const blob = new Blob(this.recordedChunks, { type: mimeType });
        resolve(blob);
      }
    });
  }

  private getMimeType(): string {
    // Check supported mime types - prioritize VP9 for better compression
    const types = [
      'video/webm;codecs=vp9', // Best compression ratio
      'video/webm;codecs=vp8,opus',
      'video/webm;codecs=vp8',
      'video/webm',
      'video/mp4'
    ];

    if (this.options.mimeType && MediaRecorder.isTypeSupported(this.options.mimeType)) {
      return this.options.mimeType;
    }

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    return 'video/webm'; // Fallback
  }

  isRecording(): boolean {
    return this.mediaRecorder?.state === 'recording';
  }
}

// Extend the DisplayMediaStreamOptions type for TypeScript
interface DisplayMediaStreamOptions extends MediaStreamConstraints {
  video?: boolean | MediaTrackConstraints & {
    cursor?: 'always' | 'motion' | 'never';
    displaySurface?: 'browser' | 'monitor' | 'window';
  };
  audio?: boolean | MediaTrackConstraints;
  preferCurrentTab?: boolean;
}